import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { breakpoints, screen } from '@/components/common/breakpoints';
import { PrimaryButton, SecondaryButton } from '@/components/common/Buttons';
import { Icon } from '@/components/common/Icon';
import { zIndex } from '@/components/common/z-index';
import { useOnClickOutside } from '@/lib/useOnClickOutside';
import { useURLQuery } from '@/lib/useURLQuery';
import { transparentize } from '@/styles/utils';

import type { icons } from '@/components/common/Icon/Icons';

interface DropdownProps {
  label: string;
  options: {
    label: string;
    value: string;
    count?: number;
  }[];
  node: any;
  onChange?: (checkedItems: string[]) => void;
  isSticky?: boolean;
  iconName?: keyof typeof icons;
  showAppliedCount?: boolean;
  clearAllFilters?: boolean;
  selectedOptions: string[];
  setClearAllFilters?: (clearAllFilters: boolean) => void;
}

export const Dropdown = ({
  label,
  options,
  node,
  onChange,
  isSticky,
  iconName,
  showAppliedCount,
  selectedOptions,
}: DropdownProps) => {
  const router = useRouter();
  const { removeParam, updateURLParams } = useURLQuery();

  const [isOpen, setIsOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const appliedCount = selectedOptions.length;
  const dropdownId = label.toLowerCase().replace(/\s+/g, '_');

  const toggle = () => setIsOpen(!isOpen);

  const handleToggleCheckbox = (event) => {
    const { checked, value } = event.target;
    const updatedCheckedItems = checked
      ? [...checkedItems, value]
      : checkedItems.filter((val) => val !== value);

    setCheckedItems(updatedCheckedItems);
  };

  const uncheckAll = useCallback(() => {
    if (checkedItems) {
      setCheckedItems([]);
    }
    removeParam(dropdownId);
  }, [checkedItems, dropdownId, removeParam]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (onChange) {
      onChange(checkedItems);
      if (checkedItems.length) {
        updateURLParams(dropdownId, checkedItems);
      }
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const searchQuery = router?.query && router.query[dropdownId];
    if (searchQuery && typeof searchQuery === 'string') {
      const checked = searchQuery.split(',');
      setCheckedItems(checked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const width = window.innerWidth;
    if (width < +breakpoints.sm) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      }
    }
    return () => {
      document.body.style.overflow = 'visible';
    };
  }, [isOpen]);

  useEffect(() => {
    setCheckedItems(selectedOptions);
  }, [selectedOptions]);

  useOnClickOutside(node, () => {
    setIsOpen(false);
  });

  return (
    <DropdownContentsContainer isSticky={isSticky} isOpen={isOpen}>
      <DropdownButton
        id={dropdownId}
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={`${dropdownId}-list`}
        aria-haspopup="true"
        data-testid={`${dropdownId}-dropdown-control`}
        isSticky={isSticky}
        isOpen={isOpen}
        hasSelection={!!appliedCount}
      >
        <div>
          {!isOpen && iconName && (
            <StyledIcon
              colour="--icon-inverse-cool-subtle"
              size="24px"
              icon={iconName}
              isSticky={isSticky}
            />
          )}
          <div>
            {label}
            {showAppliedCount && appliedCount > 0 ? (
              <AppliedCountContainer
                hasSelection={!!appliedCount}
                isOpen={isOpen}
                isSticky={isSticky}
              >
                {appliedCount}
              </AppliedCountContainer>
            ) : (
              ''
            )}
          </div>
        </div>

        <HandleContainer className="icon-container">
          {isOpen ? (
            <Icon icon="Close" aria-hidden="true" size="11px" />
          ) : (
            <Icon
              icon="ChevronDown"
              aria-hidden="true"
              size="11px"
              colour="--icon-action-dark"
            />
          )}
        </HandleContainer>
      </DropdownButton>
      <DropdownOptions
        isOpen={isOpen}
        id={`${dropdownId}-list`}
        role="listbox"
        isSticky={isSticky}
      >
        {options.map((option) => {
          return (
            <DropdownOption
              key={option.value}
              role="listitem"
              isSticky={isSticky}
            >
              <Checkbox
                type="checkbox"
                id={option.value}
                checked={checkedItems.includes(option.value)}
                name={option.value}
                value={option.value}
                onChange={handleToggleCheckbox}
                data-testid={option.value}
              />

              <DropdownOptionLabel htmlFor={option.value}>
                <DropdownOptionItems>
                  <div>{option.label}</div>
                  <DropdownJobCount>{option.count ?? null}</DropdownJobCount>
                </DropdownOptionItems>
              </DropdownOptionLabel>
            </DropdownOption>
          );
        })}
        <DropdownButtonsContainer>
          <SecondaryButton
            name={dropdownId}
            onClick={uncheckAll}
            componentTheme="light"
          >
            Clear All
          </SecondaryButton>
          <PrimaryButton
            name={dropdownId}
            onClick={handleSubmit}
            componentTheme="light"
            iconPosition="none"
          >
            Apply
          </PrimaryButton>
        </DropdownButtonsContainer>
      </DropdownOptions>
    </DropdownContentsContainer>
  );
};

interface DropdownAndOptionsProps {
  isOpen: boolean;
  isSticky: boolean;
  iconName?: string;
  hasSelection?: boolean;
}

const DropdownContentsContainer = styled.div<DropdownAndOptionsProps>`
  position: ${(props) => (props.isOpen ? 'fixed' : 'relative')};
  height: ${(props) => (props.isOpen && props.isSticky ? '100vh' : 'auto')};
  left: 0;
  top: 0;
  width: 100%;
  background: var(--surface-subtle);
  padding-top: ${(props) =>
    props.isOpen && props.isSticky ? 'var(--full-navbar-height)' : '0'};
  border-radius: ${(props) => (props.isOpen ? '0' : 'var(--radius-full)')};

  z-index: ${(props) =>
    props.isOpen ? `${zIndex.jobsFullScreenFilter}` : '1'};

  :first-of-type {
    margin-bottom: ${(props) =>
      props.isSticky ? '0' : 'var(--spacing-small)'};
  }

  ${screen.md} {
    position: relative;
    height: auto;
    padding-top: 0;
    border-radius: var(--radius-full);

    :first-of-type {
      margin-bottom: 0;
    }
  }
`;

const DropdownButton = styled.button<DropdownAndOptionsProps>`
  cursor: pointer;
  height: 48px;
  width: 100%;
  min-width: 35px;
  background: none;
  border: ${(props) =>
    props.isOpen
      ? 'none'
      : `1px solid ${transparentize('--border-cool-subtle', 50)}`};
  color: ${(props) =>
    props.isOpen || props.hasSelection
      ? 'var(--text-strong)'
      : 'var(--text-warm-subtle)'};
  font-weight: ${(props) =>
    props.isOpen
      ? 'var(--font-weight-semi-bold)'
      : 'var(--font-weight-medium)'};
  border-bottom: ${(props) =>
    props.isOpen
      ? `2px solid ${transparentize('--border-cool-subtle', 50)}`
      : `1px solid ${transparentize('--border-cool-subtle', 50)}`};
  text-align: left;
  padding-right: ${(props) =>
    props.isSticky ? 'var(--spacing-xx-small)' : 'var(--spacing-small)'};
  border-radius: ${(props) => (props.isOpen ? '0' : 'var(--radius-full)')};
  font-size: ${(props) =>
    props.isSticky && !props.isOpen ? '0px' : 'var(--font-size-x-small);'};
  text-indent: ${(props) =>
    props.isSticky || props.isOpen
      ? 'var(--spacing-100)'
      : 'var(--spacing-700)'};
  letter-spacing: var(--letter-spacing-medium);
  display: flex;
  justify-content: space-between;
  align-items: center;
  will-change: border, color, font-weight, border-bottom, padding-right,
    border-radius, font-size;

  .icon-container svg {
    display: ${(props) =>
      !props.isOpen && props.isSticky ? 'none' : 'inline'};
  }

  ${screen.md} {
    border: 1px solid ${transparentize('--border-cool-subtle', 50)};
    border-radius: var(--radius-full);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-x-small);
    text-indent: var(--spacing-large);
  }
`;

const DropdownOptions = styled.ul<DropdownAndOptionsProps>`
  position: ${(props) => (props.isOpen ? 'fixed' : 'relative')};
  height: ${(props) =>
    props.isOpen && props.isSticky ? 'calc(100vh - 114px)' : 'auto'};
  max-height: ${(props) =>
    props.isOpen && props.isSticky ? 'calc(100vh - 114px)' : '400px'};
  left: 0;
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  background: var(--background-subtle);
  box-shadow: var(--shadow-base);
  border: ${(props) =>
    props.isOpen ? 'none' : '1px solid var(--border-warm-base)'};
  flex-direction: column;
  align-items: flex-start;
  width: inherit;
  padding: var(--spacing-xx-small);
  z-index: 1;
  border-radius: var(--border-radius-tiny);
  font-weight: var(--font-weight-normal);
  margin-top: 4px;
  overflow-y: auto;
  will-change: position, height, display, border, max-height, visibility,
    margin-top;

  ${screen.md} {
    position: absolute;
    border: ${(props) =>
      props.isOpen
        ? `1px solid ${transparentize('--border-cool-subtle', 50)}`
        : '0px solid var(--border-inverse-strong)'};
    transition: all 0.2s ease-in-out;
    height: auto;
    max-height: ${(props) => (props.isOpen ? '300px' : '0')};
    opacity: ${(props) => (props.isOpen ? '1' : '0')};
    visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
    display: block;
    padding: var(--spacing-x-small) var(--spacing-x-small)
      var(--spacing-x-small) var(--spacing-xxx-small);
  }
`;

const DropdownOptionLabel = styled.label`
  width: 100%;
`;

const DropdownOption = styled.li<{ isSticky: boolean }>`
  display: flex;
  position: relative;
  padding: 10px 12px 10px 10px;
  width: 100%;
  align-items: flex-start;

  :last-of-type {
    flex-grow: ${(props) => (props.isSticky ? '1' : 'initial')};
  }
`;

const DropdownOptionItems = styled.div`
  display: inline-flex;
  flex: 1;
  justify-content: space-between;
  width: 100%;
`;

const DropdownJobCount = styled.div`
  color: var(--text-primary);
`;

const Checkbox = styled.input`
  width: 13px;
  height: 13px;
  -webkit-appearance: none;
  background: transparent;
  background-color: var(--surface-subtle);
  cursor: pointer;
  margin-right: var(--spacing-xx-small);

  ::before {
    content: '';
    display: block;
    width: inherit;
    height: inherit;
    border-radius: inherit;
    border: 0;
    box-shadow: inset 0 0 0 1px #ccd3d8;
  }
  :checked::before {
    background-repeat: no-repeat;
    background-image: url(/images/tick.svg);
    background-position: center;
    background-size: 80%;
  }
`;

const DropdownButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  border-top: 1px solid var(--border-warm-base);
  width: 100%;
  padding: 20px 12px 20px 10px;
  gap: var(--spacing-50);

  button {
    width: 100%;
    display: inline-block;
    line-height: 0;
    ${screen.md} {
      position: relative;
      padding: 0 var(--spacing-xx-small);
      height: var(--spacing-600);
    }
  }

  ${screen.sm} {
    flex-direction: row;
    gap: none;
  }
  ${screen.md} {
    padding: 20px 12px 6px 10px;
    border-top: none;
  }
`;
const AppliedCountContainer = styled.span<DropdownAndOptionsProps>`
  display: ${(props) => (!props.isSticky || props.isOpen ? 'inline' : 'block')};
  margin-left: ${(props) => (props.isSticky && !props.isOpen ? '0' : '6px')};
  font-size: ${(props) =>
    props.isSticky && !props.isOpen
      ? 'var(--font-size-xxx-small)'
      : 'var(--font-size-xx-small)'};
  border-radius: 100%;
  border: 1px solid var(--border-strong);
  background-color: var(--surface-subtle);
  line-height: ${(props) =>
    props.isSticky && !props.isOpen
      ? 'var(--line-height-normal)'
      : 'var(--line-height-large)'};
  padding: ${(props) =>
    props.isSticky && !props.isOpen
      ? '0 var(--spacing-xxx-small) 0 0'
      : 'var(--spacing-xxx-small) var(--spacing-xx-small)'};
  position: ${(props) =>
    props.isSticky && !props.isOpen ? 'absolute' : 'sticky'};
  top: -8px;
  right: -8px;
  text-indent: 4px;

  ${screen.md} {
    display: inline;
    padding: var(--spacing-xxx-small) var(--spacing-xx-small);
    margin-left: var(--spacing-xx-small);
    line-height: var(--line-height-large);
    position: inherit;
  }
`;

const HandleContainer = styled.span`
  pointer-events: none;
`;

const StyledIcon = styled(Icon)<{ isSticky }>`
  position: absolute;
  left: ${(props) => (props.isSticky ? '50%' : '25px')};
  top: ${(props) => (props.isSticky ? '50%' : '10px')};
  transform: ${(props) => (props.isSticky ? 'translate(-50%, -50%)' : 'none')};

  ${screen.md} {
    display: none;
  }
`;
